"use client";

import React, { useCallback, useMemo } from "react";
import { getEntityTableConfig } from "@/lib/entity-registry";

const TASK_CONFIG = getEntityTableConfig<ContactTaskRow>("tasks");
import { FieldRow } from "@/components/shared/InlineEditableInfoPanel/components/FieldRow";
import { useFieldEditing } from "@/components/shared/InlineEditableInfoPanel/hooks/useFieldEditing";
import { useFieldFiltering } from "@/lib/hooks/search/useFieldFiltering";
import { useTaskNotes } from "@/lib/hooks/api/tasks";
import { TaskAISummary } from "./components/TaskAISummary";
import { TaskNotesTimeline } from "@/components/entity-notes";
import { TaskModalCommentFooter } from "./TaskModalCommentFooter";
import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { Contact_TasksDetail } from "@/lib/types/task-extensions";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

const TASK_CONFIG_TYPED = TASK_CONFIG as SimpleTableConfig<Contact_TasksDetail>;

interface TaskModalDetailsPrimaryProps {
  task: Contact_TasksDetail;
  onInlineEdit: (item: Contact_TasksDetail) => Promise<void>;
}

export function TaskModalDetailsPrimary({ task, onInlineEdit }: TaskModalDetailsPrimaryProps) {
  const { filterFieldsByRole, roleName } = useFieldFiltering();
  const { data: liveTaskNotes } = useTaskNotes(task.id);

  const descriptionFieldConfig = useMemo(() => {
    const roleFiltered = filterFieldsByRole(TASK_CONFIG_TYPED.fields);
    return roleFiltered.description;
  }, [filterFieldsByRole]);

  const {
    editingField,
    editedItem,
    handleFieldEdit,
    handleSaveField,
    handleCancelEdit,
    handleFieldValueChange,
  } = useFieldEditing(task, onInlineEdit);

  const canEdit = TASK_CONFIG_TYPED.actions?.edit !== false && roleName !== "client_user";

  const memoizedFieldEdit = useCallback((fieldId: string) => handleFieldEdit(fieldId), [handleFieldEdit]);
  const memoizedSaveField = useCallback(
    (fieldId: string) => handleSaveField(fieldId),
    [handleSaveField]
  );
  const memoizedCancel = useCallback(() => handleCancelEdit(), [handleCancelEdit]);
  const memoizedValueChange = useCallback(
    (fieldId: string, newValue: unknown) => handleFieldValueChange(fieldId, newValue),
    [handleFieldValueChange]
  );

  if (!descriptionFieldConfig) {
    return null;
  }

  const aiMinimal = TASK_CONFIG_TYPED.detailModalUi?.aiSummaryMinimalProminence === true;

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Task details</h3>
        <FieldRow<Contact_TasksDetail>
          fieldId="description"
          fieldConfig={descriptionFieldConfig}
          value={editedItem.description}
          isFieldEditing={editingField === "description"}
          canEdit={canEdit}
          isFullscreen={false}
          item={task}
          config={TASK_CONFIG_TYPED}
          onFieldEdit={memoizedFieldEdit}
          onSaveField={memoizedSaveField}
          onCancelEdit={memoizedCancel}
          onFieldValueChange={memoizedValueChange}
        />
      </div>

      <div className="space-y-2">
        <TaskNotesTimeline taskId={task.id} maxHeight="260px" />
        <TaskModalCommentFooter taskId={task.id} />
      </div>

      <div className={aiMinimal ? "pt-1 border-t border-border/60" : "space-y-3"}>
        {!aiMinimal ? (
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI summary</h3>
        ) : null}
        <TaskAISummary
          taskId={task.id}
          taskTitle={task.title ?? ""}
          taskDescription={task.description ?? undefined}
          taskNotes={(liveTaskNotes ?? []).map((note) => ({
            ...note,
            note_type: note.note_type ?? "general",
            is_internal: Boolean(note.is_internal),
          }))}
          autoGenerate={false}
          showMetadata={true}
          showRefreshButton={true}
          showClearButton={false}
          compact={false}
          minimalProminence={aiMinimal}
          className="w-full"
          onSummaryGenerated={() => {}}
          onError={() => {}}
        />
      </div>
    </div>
  );
}
